import { useState, useEffect, useCallback, useRef } from "react";
import { usePermissions } from "./PermissionsContext";
import { createPortal } from "react-dom";
import "./index.css";

// ─── (All helpers, API, map components, form components are unchanged) ─────────

const API_BASE = "https://api.seritonex.com";

const SA_PROVINCES = [
  { value: "", label: "— Select Province —" },
  { value: "Eastern Cape", label: "Eastern Cape" },
  { value: "Free State", label: "Free State" },
  { value: "Gauteng", label: "Gauteng" },
  { value: "KwaZulu-Natal", label: "KwaZulu-Natal" },
  { value: "Limpopo", label: "Limpopo" },
  { value: "Mpumalanga", label: "Mpumalanga" },
  { value: "North West", label: "North West" },
  { value: "Northern Cape", label: "Northern Cape" },
  { value: "Western Cape", label: "Western Cape" },
];

const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function apiFetch(method, path, body = null) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: authHeaders(),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    return null;
  }
}

function toArray(data, ...keys) {
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    const val = data?.[key];
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object") return [val];
  }
  if (data && typeof data === "object" && !Array.isArray(data)) return [data];
  return [];
}

function resolveUserId(userOrId) {
  if (userOrId == null) return null;
  if (typeof userOrId === "number" && !isNaN(userOrId)) return userOrId;
  if (typeof userOrId === "string") {
    const n = Number(userOrId);
    if (!isNaN(n) && n > 0) return n;
  }
  if (typeof userOrId === "object") {
    const raw = userOrId._rawUserId ?? userOrId.user_id ?? userOrId.id;
    if (raw != null && !isNaN(Number(raw)) && Number(raw) > 0)
      return Number(raw);
  }
  return null;
}

function getOrgIdFromToken() {
  for (const store of [localStorage, sessionStorage]) {
    try {
      const raw = store.getItem("user");
      if (!raw) continue;
      const user = JSON.parse(raw);
      const fromUser =
        user?.organization_id ??
        user?.organization?.organization_id ??
        user?.tokenClaims?.organization?.organization_id;
      if (fromUser != null && !isNaN(Number(fromUser))) {
        return Number(fromUser);
      }
    } catch {}
  }
  try {
    const token = localStorage.getItem("token") || "";
    if (token) {
      const base64Url = token.split(".")[1] || "";
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(base64));
      const fromToken =
        payload?.organization?.organization_id ??
        payload?.organization_id ??
        payload?.org_id ??
        payload?.orgId;
      if (fromToken != null && !isNaN(Number(fromToken))) {
        return Number(fromToken);
      }
    }
  } catch {}

  console.warn(
    "getOrgIdFromToken: couldn't resolve organization_id from user or token — defaulting to 1",
  );
  return 1;
}

let leafletPromise = null;
function loadLeaflet() {
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve) => {
    if (window.L) {
      resolve(window.L);
      return;
    }
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => resolve(window.L);
    document.head.appendChild(script);
  });
  return leafletPromise;
}

async function geocodeAddress(query) {
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, {
      headers: { "Accept-Language": "en", "User-Agent": "AMIS-App/1.0" },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.map((item) => ({
      display: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      addr1: [item.address?.house_number, item.address?.road]
        .filter(Boolean)
        .join(" "),
      addr2: item.address?.suburb || item.address?.neighbourhood || "",
      city:
        item.address?.city ||
        item.address?.town ||
        item.address?.village ||
        item.address?.municipality ||
        "",
      region:
        item.address?.state ||
        item.address?.province ||
        item.address?.region ||
        "",
      postal: item.address?.postcode || "",
      country: item.address?.country || "",
    }));
  } catch {
    return [];
  }
}

async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`;
    const res = await fetch(url, {
      headers: { "Accept-Language": "en", "User-Agent": "AMIS-App/1.0" },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function LeafletMap({
  center,
  zoom = 13,
  onClickPin,
  markerPos,
  interactive = true,
  draggableMarker = false,
}) {
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const containerId = useRef(`amp-map-${Math.random().toString(36).slice(2)}`);
  const onClickPinRef = useRef(onClickPin);
  useEffect(() => {
    onClickPinRef.current = onClickPin;
  }, [onClickPin]);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet().then((L) => {
      if (cancelled || mapInstanceRef.current) return;
      const map = L.map(containerId.current, {
        center: center || [-29.0, 25.0],
        zoom,
        zoomControl: true,
        attributionControl: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);
      if (interactive || draggableMarker) {
        map.on("click", (e) =>
          onClickPinRef.current?.(e.latlng.lat, e.latlng.lng),
        );
        map.getContainer().style.cursor = "crosshair";
      }
      mapInstanceRef.current = map;
    });
    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    loadLeaflet().then((L) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      if (markerPos) {
        const icon = L.divIcon({
          className: "",
          html: `<div style="width:18px;height:18px;background:#e24b4a;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 18],
        });
        if (markerRef.current) {
          markerRef.current.setLatLng([markerPos.lat, markerPos.lng]);
          if (draggableMarker) markerRef.current.dragging?.enable();
          else markerRef.current.dragging?.disable();
        } else {
          const m = L.marker([markerPos.lat, markerPos.lng], {
            icon,
            draggable: draggableMarker,
          }).addTo(map);
          if (draggableMarker) {
            m.on("dragend", () => {
              const ll = m.getLatLng();
              const rLat = Math.round(ll.lat * 1e6) / 1e6,
                rLng = Math.round(ll.lng * 1e6) / 1e6;
              map.getContainer().style.cursor = "crosshair";
              onClickPinRef.current?.(rLat, rLng);
            });
          }
          markerRef.current = m;
        }
        map.setView(
          [markerPos.lat, markerPos.lng],
          Math.max(map.getZoom(), 14),
        );
      } else if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    });
  }, [markerPos, draggableMarker]);

  return (
    <div
      id={containerId.current}
      style={{
        height: 400,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid var(--border)",
        marginBottom: 12,
      }}
    />
  );
}

function AddressMapPicker({ value = {}, onChange, required = false }) {
  const [mode, setMode] = useState(value.mode || "search");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [loc, setLoc] = useState({
    confirmed: false,
    lat: null,
    lng: null,
    addr1: "",
    addr2: "",
    city: "",
    region: "",
    postal: "",
    country: "",
    label: "",
    unitNumber: "",
    ...value,
  });
  const [pinAdjusted, setPinAdjusted] = useState(false);

  const emit = useCallback(
    (update) => {
      const next = { ...loc, ...update, mode };
      setLoc(next);
      onChange?.(next);
    },
    [loc, mode, onChange],
  );

  function switchMode(m) {
    setMode(m);
    setSearchResults([]);
    setSearchError("");
    setPinAdjusted(false);
    const reset = {
      confirmed: false,
      lat: null,
      lng: null,
      addr1: "",
      addr2: "",
      city: "",
      region: "",
      postal: "",
      country: "",
      label: "",
      unitNumber: "",
      mode: m,
    };
    setLoc(reset);
    onChange?.(reset);
  }

  async function doSearch() {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchError("");
    setSearchResults([]);
    const results = await geocodeAddress(searchQuery);
    if (results.length === 0)
      setSearchError(
        "No results found. Try a more specific address or use Drop Pin mode.",
      );
    setSearchResults(results);
    setSearching(false);
  }

  function selectResult(r) {
    setSearchResults([]);
    setSearchQuery(r.display);
    setPinAdjusted(false);
    const matchedProvince =
      SA_PROVINCES.find(
        (p) =>
          p.value && r.region?.toLowerCase().includes(p.value.toLowerCase()),
      )?.value || r.region;
    emit({
      confirmed: true,
      lat: r.lat,
      lng: r.lng,
      addr1: r.addr1,
      addr2: r.addr2,
      city: r.city,
      region: matchedProvince,
      postal: r.postal,
      country: r.country,
    });
  }

  function clearSelection() {
    setSearchQuery("");
    setSearchResults([]);
    setPinAdjusted(false);
    emit({
      confirmed: false,
      lat: null,
      lng: null,
      addr1: "",
      addr2: "",
      city: "",
      region: "",
      postal: "",
      country: "",
      unitNumber: "",
    });
  }

  async function handleMapClick(lat, lng) {
    const rLat = Math.round(lat * 1e6) / 1e6,
      rLng = Math.round(lng * 1e6) / 1e6;
    if (mode === "search") {
      setPinAdjusted(true);
      emit({ confirmed: true, lat: rLat, lng: rLng });
    } else {
      const rev = await reverseGeocode(rLat, rLng);
      const autoLabel = rev?.display_name
        ? rev.display_name.split(",").slice(0, 3).join(", ")
        : `${rLat}, ${rLng}`;
      emit({
        confirmed: true,
        lat: rLat,
        lng: rLng,
        label: loc.label || autoLabel,
      });
    }
  }

  function editField(key, val) {
    const next = { ...loc, [key]: val, confirmed: false, mode };
    setLoc(next);
    onChange?.(next);
  }

  const isLocked = loc.confirmed && mode === "search";

  // ── all colours now use CSS custom properties so they theme-switch correctly ──
  const inputStyle = (locked) => ({
    width: "100%",
    height: 34,
    padding: "0 10px",
    border: `1px ${locked ? "dashed" : "solid"} var(--border)`,
    borderRadius: 8,
    fontSize: 13,
    background: locked ? "var(--elevated)" : "var(--deep)",
    color: locked ? "var(--text3)" : "var(--text1)",
    outline: "none",
    cursor: locked ? "not-allowed" : "text",
    boxSizing: "border-box",
  });

  const selectStyle = (locked) => ({
    width: "100%",
    height: 34,
    padding: "0 10px",
    border: `1px ${locked ? "dashed" : "solid"} var(--border)`,
    borderRadius: 8,
    fontSize: 13,
    background: locked ? "var(--elevated)" : "var(--deep)",
    color: locked ? "var(--text3)" : "var(--text1)",
    outline: "none",
    cursor: locked ? "not-allowed" : "default",
    boxSizing: "border-box",
    appearance: "auto",
  });

  // reusable inline pill styles
  const pillSuccess = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    padding: "3px 8px",
    borderRadius: 6,
    background: "rgba(0,230,118,.08)",
    color: "var(--success)",
    border: "1px solid rgba(0,230,118,.2)",
  };
  const pillAmber = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 500,
    padding: "3px 8px",
    borderRadius: 6,
    background: "rgba(255,171,0,.08)",
    color: "var(--amber)",
    border: "1px solid rgba(255,171,0,.2)",
  };
  const ghostBtn = {
    padding: "2px 8px",
    fontSize: 11,
    cursor: "pointer",
    border: "1px solid var(--border)",
    borderRadius: 6,
    background: "transparent",
    color: "var(--text3)",
  };
  const coordBar = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 10px",
    background: "var(--deep)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    fontFamily: "monospace",
    fontSize: 11,
    color: "var(--text2)",
    marginBottom: 12,
  };

  return (
    <div>
      {/* ── mode toggle ─────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          border: "1px solid var(--border)",
          borderRadius: 8,
          overflow: "hidden",
          marginBottom: 14,
        }}
      >
        {["search", "pin"].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => switchMode(m)}
            style={{
              flex: 1,
              padding: "8px 12px",
              fontSize: 12,
              fontWeight: mode === m ? 600 : 500,
              cursor: "pointer",
              border: "none",
              borderRight: m === "search" ? "1px solid var(--border)" : "none",
              background: mode === m ? "var(--elevated)" : "var(--deep)",
              color: mode === m ? "var(--text1)" : "var(--text3)",
              transition: "background .14s, color .14s",
            }}
          >
            {m === "search" ? "🔍 Search address" : "📍 Drop pin on map"}
          </button>
        ))}
      </div>

      {/* ── SEARCH MODE ─────────────────────────────────────────── */}
      {mode === "search" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              style={{ ...inputStyle(isLocked), flex: 1, width: "auto" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Type an address, landmark or area…"
              disabled={isLocked}
            />
            <button
              type="button"
              onClick={isLocked ? clearSelection : doSearch}
              disabled={searching}
              style={{
                height: 34,
                padding: "0 14px",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                background: "var(--elevated)",
                color: "var(--text1)",
                whiteSpace: "nowrap",
                transition: "border-color .14s",
              }}
            >
              {isLocked ? "Clear" : searching ? "Searching…" : "Search"}
            </button>
          </div>

          {searchError && (
            <div
              style={{
                padding: "6px 10px",
                marginBottom: 8,
                borderRadius: 6,
                background: "rgba(255,23,68,.08)",
                border: "1px solid rgba(255,23,68,.25)",
                color: "var(--danger)",
                fontSize: 11,
              }}
            >
              {searchError}
            </div>
          )}

          {searchResults.length > 0 && (
            <div
              style={{
                border: "1px solid var(--border)",
                borderRadius: 8,
                overflow: "hidden",
                marginBottom: 10,
                background: "var(--elevated)",
              }}
            >
              {searchResults.map((r, i) => (
                <div
                  key={i}
                  onClick={() => selectResult(r)}
                  style={{
                    padding: "9px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                    borderBottom:
                      i < searchResults.length - 1
                        ? "1px solid var(--border)"
                        : "none",
                    lineHeight: 1.4,
                    color: "var(--text1)",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "var(--hover)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  {r.display}
                </div>
              ))}
            </div>
          )}

          {loc.confirmed && loc.lat != null && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 10px",
                  marginBottom: 6,
                  borderRadius: 6,
                  background: "rgba(255,171,0,.08)",
                  border: "1px solid rgba(255,171,0,.2)",
                  fontSize: 11,
                  color: "var(--amber)",
                  lineHeight: 1.4,
                }}
              >
                <span style={{ fontSize: 13 }}>📍</span>
                <span>
                  <strong>Drag the pin</strong> or{" "}
                  <strong>click anywhere on the map</strong> to fine-tune the
                  exact location.
                </span>
              </div>

              <LeafletMap
                center={[loc.lat, loc.lng]}
                markerPos={{ lat: loc.lat, lng: loc.lng }}
                interactive
                draggableMarker
                onClickPin={handleMapClick}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <span style={pillSuccess}>✓ Location confirmed</span>
                <button type="button" onClick={clearSelection} style={ghostBtn}>
                  Change
                </button>
              </div>

              <div style={coordBar}>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 10,
                    color: "var(--text3)",
                  }}
                >
                  LAT
                </span>
                {loc.lat}
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 10,
                    color: "var(--text3)",
                    marginLeft: 8,
                  }}
                >
                  LNG
                </span>
                {loc.lng}
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    color: "var(--text3)",
                  }}
                >
                  {pinAdjusted ? "📍 manually adjusted" : "from geocode"}
                </span>
              </div>
            </>
          )}

          {/* Unit number */}
          <div style={{ marginBottom: 10 }}>
            <div className="fl">
              Unit Number{" "}
              <span style={{ color: "var(--danger)", marginLeft: 2 }}>*</span>
            </div>
            <input
              style={{ ...inputStyle(false), width: "100%" }}
              value={loc.unitNumber || ""}
              onChange={(e) => {
                const next = { ...loc, unitNumber: e.target.value, mode };
                setLoc(next);
                onChange?.(next);
              }}
              placeholder="e.g. Unit 4B, Flat 12, Suite 300"
            />
          </div>

          {/* Address fields */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div className="fl">Address line 1</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.addr1}
                onChange={(e) => editField("addr1", e.target.value)}
                disabled={isLocked}
                placeholder="123 Main St"
              />
            </div>
            <div>
              <div className="fl">City</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.city}
                onChange={(e) => editField("city", e.target.value)}
                disabled={isLocked}
                placeholder="City"
              />
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div className="fl">Address line 2</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.addr2}
                onChange={(e) => editField("addr2", e.target.value)}
                disabled={isLocked}
                placeholder="Suite / Unit"
              />
            </div>
            <div>
              <div className="fl">Province</div>
              <select
                style={selectStyle(isLocked)}
                value={loc.region}
                onChange={(e) => editField("region", e.target.value)}
                disabled={isLocked}
              >
                {SA_PROVINCES.map((p) => (
                  <option
                    key={p.value}
                    value={p.value}
                    style={{ background: "var(--deep)", color: "var(--text1)" }}
                  >
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div className="fl">Postal code</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.postal}
                onChange={(e) => editField("postal", e.target.value)}
                disabled={isLocked}
                placeholder="1234"
              />
            </div>
            <div>
              <div className="fl">Country</div>
              <input
                style={inputStyle(isLocked)}
                value={loc.country}
                onChange={(e) => editField("country", e.target.value)}
                disabled={isLocked}
                placeholder="South Africa"
              />
            </div>
          </div>

          {!loc.confirmed && (
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                display: "flex",
                alignItems: "flex-start",
                gap: 6,
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              ℹ️ You can type address fields manually, but searching and
              confirming on the map is recommended for accuracy.
            </div>
          )}
        </>
      )}

      {/* ── PIN MODE ────────────────────────────────────────────── */}
      {mode === "pin" && (
        <>
          {!loc.confirmed ? (
            <div style={{ ...pillAmber, marginBottom: 10 }}>
              Click anywhere on the map to drop your pin
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <span style={pillSuccess}>✓ Pin placed</span>
              <button
                type="button"
                onClick={() =>
                  emit({ confirmed: false, lat: null, lng: null, label: "" })
                }
                style={ghostBtn}
              >
                Repin
              </button>
            </div>
          )}

          <LeafletMap
            center={loc.lat ? [loc.lat, loc.lng] : [-29.0, 25.0]}
            zoom={loc.lat ? 14 : 5}
            markerPos={loc.lat ? { lat: loc.lat, lng: loc.lng } : null}
            onClickPin={handleMapClick}
            interactive
            draggableMarker={loc.confirmed}
          />

          {loc.lat != null && (
            <div style={coordBar}>
              <span
                style={{ fontWeight: 600, fontSize: 10, color: "var(--text3)" }}
              >
                lat
              </span>
              {loc.lat}
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 10,
                  color: "var(--text3)",
                  marginLeft: 8,
                }}
              >
                lng
              </span>
              {loc.lng}
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 10,
                  color: "var(--text3)",
                }}
              >
                drag pin or click map to reposition
              </span>
            </div>
          )}

          {/* Location label */}
          <div style={{ marginBottom: 10 }}>
            <div className="fl">
              Location label{" "}
              {required && (
                <span style={{ color: "var(--danger)", marginLeft: 2 }}>*</span>
              )}
            </div>
            <input
              style={{ ...inputStyle(false), width: "100%" }}
              value={loc.label}
              onChange={(e) => {
                const next = { ...loc, label: e.target.value, mode };
                setLoc(next);
                onChange?.(next);
              }}
              placeholder="e.g. Kruger Park North Gate, Mine Shaft 4, Farm Storage A…"
            />
            <div
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginTop: 6,
                lineHeight: 1.5,
              }}
            >
              ℹ️ No street address needed. Pin coordinates will be stored. Add a
              descriptive label so staff know what this location is.
            </div>
          </div>

          {/* Unit number */}
          <div style={{ marginBottom: 10 }}>
            <div className="fl">
              Unit Number{" "}
              <span style={{ color: "var(--danger)", marginLeft: 2 }}>*</span>
            </div>
            <input
              style={{ ...inputStyle(false), width: "100%" }}
              value={loc.unitNumber || ""}
              onChange={(e) => {
                const next = { ...loc, unitNumber: e.target.value, mode };
                setLoc(next);
                onChange?.(next);
              }}
              placeholder="e.g. Unit 4B, Flat 12, Suite 300"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── User API ──────────────────────────────────────────────────────────────────
export const UserAPI = {
  getAll: async () => {
    const data = await apiFetch("GET", "/api/v1/amis/user/all");
    if (!data) return [];
    const arr = toArray(data, "users", "data", "items");
    return arr.map((u) => ({
      id: u.user_id ?? u.id ?? null,
      user_id: u.user_id ?? u.id ?? null,
      name:
        [u.first_name, u.last_name].filter(Boolean).join(" ") ||
        u.name ||
        u.username ||
        `User ${u.user_id ?? u.id}`,
      email: u.email ?? "",
      _raw: u,
    }));
  },
};

// ─── Warehouse API ─────────────────────────────────────────────────────────────
export const WarehouseAPI = {
  getAll: async () => {
    const data = await apiFetch("GET", "/api/v1/amis/warehouse/get-all");
    if (!data) return null;
    const arr = Array.isArray(data.warehouses)
      ? data.warehouses
      : Array.isArray(data)
        ? data
        : data.data || [];
    return arr.map(mapWarehouse);
  },
  getByBranch: async (branchId) => {
    const data = await apiFetch(
      "POST",
      "/api/v1/amis/warehouse/get-by-branch-id",
      { branch_id: Number(branchId) },
    );
    if (!data) return null;
    const arr = Array.isArray(data.warehouses)
      ? data.warehouses
      : Array.isArray(data)
        ? data
        : data.data || [];
    return arr.map(mapWarehouse);
  },
  getById: async (id) => {
    const data = await apiFetch("POST", "/api/v1/amis/warehouse/get-by-id", {
      warehouse_id: id,
    });
    return data?.warehouse || data?.data || data || null;
  },
  getByUser: (user_id) => {
    const numId = resolveUserId(user_id);
    return apiFetch("POST", "/api/v1/amis/warehouse/by-user", {
      user_id: numId,
    });
  },
  create: (d) => apiFetch("POST", "/api/v1/amis/warehouse/create", d),
  update: (id, d) =>
    apiFetch("POST", "/api/v1/amis/warehouse/update", {
      warehouse_id: id,
      ...d,
    }),
  delete: (id) =>
    apiFetch("POST", "/api/v1/amis/warehouse/delete", { warehouse_id: id }),
  bind: (warehouseId, userId) => {
    const numUserId = resolveUserId(userId);
    return apiFetch("POST", "/api/v1/amis/warehouse/bind", {
      warehouse_id: warehouseId,
      user_id: numUserId,
    });
  },
  unbind: (warehouseId, userId) => {
    const numUserId = resolveUserId(userId);
    return apiFetch("POST", "/api/v1/amis/warehouse/unbind", {
      warehouse_id: warehouseId,
      user_id: numUserId,
    });
  },
  getTypes: async () => {
    const data = await apiFetch("GET", "/api/v1/amis/warehouse/types");
    if (!data) return null;
    const arr = Array.isArray(data.warehouse_types)
      ? data.warehouse_types
      : Array.isArray(data.warehouseTypes)
        ? data.warehouseTypes
        : Array.isArray(data)
          ? data
          : data.data || [];
    return arr.map((t) => ({
      id: t.warehouse_type_id || t.id || null,
      description: t.description || "",
    }));
  },
  getTypeById: (id) =>
    apiFetch("POST", "/api/v1/amis/warehouse/type-by-id", {
      warehouse_type_id: id,
    }),
  createType: (d) => apiFetch("POST", "/api/v1/amis/warehouse/create-type", d),
  updateType: (id, d) =>
    apiFetch("POST", "/api/v1/amis/warehouse/update-type", {
      warehouse_type_id: id,
      ...d,
    }),
  deleteType: (id) =>
    apiFetch("POST", "/api/v1/amis/warehouse/delete-type", {
      warehouse_type_id: id,
    }),
};

function mapWarehouse(w) {
  return {
    id: w.warehouse_id || w.id || "",
    name: w.warehouse_name || w.name || "",
    location: w.warehouse_type?.description || "",
    branch: w.branch?.branch_name || w.branch?.name || "",
    status: w.is_active ? "Active" : "Inactive",
    warehouse_type_id: w.warehouse_type?.warehouse_type_id || null,
    branch_id: w.branch?.branch_id || null,
    _raw: w,
  };
}

export const BranchAPI = {
  getAll: async () => {
    const data = await apiFetch("GET", "/api/v1/amis/branch/get-all");
    if (!data) return null;
    const arr = Array.isArray(data) ? data : data.branches || data.data || [];
    return arr.map((b) => ({ id: b.branch_id, name: b.branch_name || "" }));
  },
};

// ─── UI primitives ───────────────────────────────────────────────────────────
function Badge({ children, color = "green" }) {
  return <span className={`badge badge-${color}`}>{children}</span>;
}
function Btn({
  children,
  variant = "p",
  sm,
  onClick,
  disabled,
  type = "button",
  fullWidth,
}) {
  return (
    <button
      type={type}
      className={`btn btn-${variant}${sm ? " btn-sm" : ""}${fullWidth ? " btn-full" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}
function FG({ label, children, hint, required }) {
  return (
    <div className="fg">
      <div className="fl">
        {label}
        {required && <span style={{ color: "#e05260", marginLeft: 2 }}>*</span>}
      </div>
      {children}
      {hint && <div className="tc3 fs10 mt-4">{hint}</div>}
    </div>
  );
}
function Inp({ value, onChange, placeholder, type = "text", disabled }) {
  return (
    <input
      className="fi"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      disabled={disabled}
    />
  );
}
function Sel({ value, onChange, options, disabled }) {
  return (
    <select
      className="fsel"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="tsearch">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div
        className={`modal${wide ? " modal-lg" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhdr">
          <span className="mtitle">{title}</span>
          <Btn variant="g" sm onClick={onClose}>
            ✕
          </Btn>
        </div>
        <div className="mbody">{children}</div>
      </div>
    </div>
  );
}
function DataTable({ columns, rows }) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="empty"
                style={{
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  minHeight: 200,
                }}
              ></td>
            </tr>
          ) : (
            rows
          )}
        </tbody>
      </table>
    </div>
  );
}
function ReviewBlock({ data }) {
  return (
    <div className="review-block">
      {Object.entries(data)
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <div key={k} className="review-row">
            <span className="review-label">{k}</span>
            <span className="review-value">{String(v)}</span>
          </div>
        ))}
    </div>
  );
}
function FormCard({ icon, label, children }) {
  return (
    <div className="panel mb-16">
      <div className="ph">
        {icon && <span className="fs-16">{icon}</span>}
        <span className="ph-title">{label}</span>
      </div>
      <div className="pb">{children}</div>
    </div>
  );
}
function FormFooter({ onCancel, onSubmit, loading, label = "Save", danger }) {
  return (
    <div className="fb gap-8 pt-16" style={{ marginBottom: 25 }}>
      <Btn variant="g" onClick={onCancel} disabled={loading}>
        Cancel
      </Btn>
      <Btn variant={danger ? "d" : "p"} onClick={onSubmit} disabled={loading}>
        {loading ? <span className="spin-icon" /> : label}
      </Btn>
    </div>
  );
}
function PageHeader({ title, subtitle, subject, onBack }) {
  return <div className="mb-22"></div>;
}
function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef(null),
    buttonRef = useRef(null),
    menuRef = useRef(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    const h = (e) => {
      if (!open) return;
      if (
        wrapperRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const handleToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 170;
      const left = Math.min(
        Math.max(rect.left, 8),
        window.innerWidth - menuWidth - 8,
      );
      setPos({ top: rect.bottom + 8, left });
    }
    setOpen((prev) => !prev);
  }, []);
  const menu = open ? (
    <div
      className="adrop"
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        right: "auto",
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, i) => (
        <div
          key={i}
          className={`adrop-item${item.danger ? " red" : ""}`}
          onClick={() => {
            setOpen(false);
            item.onClick?.();
          }}
        >
          {item.label}
        </div>
      ))}
    </div>
  ) : null;
  return (
    <div className="action-wrap" ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-g btn-sm"
        onClick={handleToggle}
      >
        •••
      </button>
      {mounted && menu && createPortal(menu, document.body)}
    </div>
  );
}
function UserPicker({ users, label = "Select User", value, onChange }) {
  const opts = [
    { value: "", label: `— ${label} —` },
    ...users
      .filter((u) => u.id != null)
      .map((u) => ({
        value: String(u.id),
        label: `${u.name}${u.email ? ` (${u.email})` : ""}`,
      })),
  ];
  return <Sel value={value} onChange={onChange} options={opts} />;
}

// ─── AddWarehousePage ──────────────────────────────────────────────────────────
function AddWarehousePage({ branches, onSave, onBack }) {
  const [d, setD] = useState({
    warehouse_name: "",
    warehouse_type_id: "",
    branch_id: "",
    address_code: "",
  });
  const [types, setTypes] = useState([]);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locError, setLocError] = useState("");
  const upd = (k, v) => setD((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    WarehouseAPI.getTypes().then((t) => {
      setTypes(t || []);
      if (Array.isArray(t) && t.length > 0)
        setD((x) => ({
          ...x,
          warehouse_type_id: x.warehouse_type_id || String(t[0].id),
        }));
    });
  }, []);

  async function submit() {
    if (!d.warehouse_name.trim()) {
      setError("Warehouse name is required.");
      return;
    }
    if (!location || (!location.confirmed && !location.addr1?.trim())) {
      setLocError("Please search for an address or drop a pin on the map.");
      return;
    }
    setError(null);
    setLocError("");
    setLoading(true);
    const addressPayload =
      location.mode === "pin"
        ? {
            address_code: d.address_code || "",
            address_line_1: location.label || "",
            address_line_2: "",
            city: "",
            region: "",
            postal_code: "",
            country: "",
            geo_location: { lat: location.lat, lng: location.lng },
          }
        : {
            address_code: d.address_code || "",
            address_line_1: location.addr1 || "",
            address_line_2: location.addr2 || "",
            city: location.city || "",
            region: location.region || "",
            postal_code: location.postal || "",
            country: location.country || "",
            geo_location: {
              lat: location.lat || null,
              lng: location.lng || null,
            },
          };
    const finalPayload = {
      warehouse_name: d.warehouse_name.trim(),
      warehouse_type_id: d.warehouse_type_id
        ? Number(d.warehouse_type_id)
        : (types[0]?.id ?? 1),
      branch_id: d.branch_id ? Number(d.branch_id) : null,
      address: addressPayload,
    };
    const res = await WarehouseAPI.create(finalPayload);
    onSave({
      id: res?.warehouseId ?? res?.warehouse_id ?? Date.now(),
      name: d.warehouse_name,
      branch:
        branches.find((b) => String(b.id) === String(d.branch_id))?.name || "",
      status: "Active",
      _raw: res,
    });
    setLoading(false);
  }

  const branchOpts = [
    { value: "", label: "— Select branch —" },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const typeOpts = [
    { value: "", label: "— Select type —" },
    ...(types || []).map((t) => ({
      value: String(t.id),
      label: t.description,
    })),
  ];

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Add New Warehouse"
        subtitle="Register a storage facility and link it to a branch"
        onBack={onBack}
      />
      {error && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: 14,
            borderRadius: 8,
            background: "rgba(220,53,69,.1)",
            border: "1px solid rgba(220,53,69,.3)",
            color: "#e05260",
            fontSize: 12,
          }}
        >
          ⚠️ {error}
        </div>
      )}
      <FormCard label="Warehouse Details">
        <FG label="Warehouse Name" required>
          <Inp
            value={d.warehouse_name}
            onChange={(v) => upd("warehouse_name", v)}
            placeholder="New Warehouse"
          />
        </FG>
        <div className="g2">
          <FG label="Branch" required>
            <Sel
              value={d.branch_id}
              onChange={(v) => upd("branch_id", v)}
              options={branchOpts}
            />
          </FG>
          <FG label="Warehouse Type" required>
            <Sel
              value={d.warehouse_type_id}
              onChange={(v) => upd("warehouse_type_id", v)}
              options={typeOpts}
            />
          </FG>
        </div>
        <FG label="Address Code">
          <Inp
            value={d.address_code}
            onChange={(v) => upd("address_code", v)}
            placeholder="ADDR001"
          />
        </FG>
      </FormCard>
      <FormCard icon="📍" label="Location & Address">
        {locError && (
          <div
            style={{
              padding: "8px 12px",
              marginBottom: 12,
              borderRadius: 8,
              background: "rgba(220,53,69,.08)",
              border: "0.5px solid rgba(220,53,69,.3)",
              color: "#e05260",
              fontSize: 12,
            }}
          >
            ⚠️ {locError}
          </div>
        )}
        <AddressMapPicker
          value={location || {}}
          onChange={(data) => {
            setLocation(data);
            if (locError && (data.confirmed || data.addr1?.trim()))
              setLocError("");
          }}
          required
        />
        <div style={{ height: 12 }} />
        <FG label="Province">
          <Sel
            value={d.region || ""}
            onChange={(v) => upd("region", v)}
            options={SA_PROVINCES}
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Create Warehouse"
      />
    </div>
  );
}

// ─── EditWarehousePage ─────────────────────────────────────────────────────────
function EditWarehousePage({ warehouse, branches, onSave, onBack }) {
  const [d, setD] = useState({
    ...warehouse,
    cap: warehouse.capacity?.replace(/\D.*$/, "") || "",
  });
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const upd = (k, v) => setD((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    WarehouseAPI.getTypes().then((t) => setTypes(t || []));
  }, []);

  useEffect(() => {
    let mounted = true;
    WarehouseAPI.getById(warehouse.id).then((res) => {
      if (!res || !mounted) return;
      const w = res.warehouse_id != null ? res : res.warehouse || res;
      setD((x) => ({
        ...x,
        name: w.warehouse_name || x.name,
        status: w.is_active ? "Active" : "Inactive",
        cap: w.current_inventory != null ? String(w.current_inventory) : x.cap,
        address_code: w.address?.address_code || x.address_code,
        address_id: w.address?.address_id || x.address_id,
        addr1: w.address?.address_line_1 || x.addr1,
        addr2: w.address?.address_line_2 || x.addr2,
        city: w.address?.city || x.city,
        region: w.address?.region || x.region,
        postal: w.address?.postal_code || x.postal,
        country: w.address?.country || x.country,
        type_description:
          w.warehouse_type?.description || x.type_description || "",
        warehouse_type_id: w.warehouse_type?.warehouse_type_id
          ? String(w.warehouse_type.warehouse_type_id)
          : x.warehouse_type_id || "",
        branch: w.branch?.branch_name || x.branch,
        branch_id: w.branch?.branch_id ?? x.branch_id ?? null,
      }));
      const addr = w.address || {};
      setLocation({
        confirmed: !!(addr.address_line_1 || addr.latitude || addr.longitude),
        mode: addr.latitude && addr.longitude ? "pin" : "search",
        lat: addr.latitude || null,
        lng: addr.longitude || null,
        addr1: addr.address_line_1 || "",
        addr2: addr.address_line_2 || "",
        city: addr.city || "",
        region: addr.region || "",
        postal: addr.postal_code || "",
        country: addr.country || "",
        label: addr.address_line_1 || "",
        unitNumber: "",
      });
    });
    return () => (mounted = false);
  }, [warehouse.id]);

  const branchOpts = [
    { value: "", label: "— Select Branch —" },
    ...branches.map((b) => ({ value: b.name, label: b.name })),
  ];
  const typeOpts = [
    { value: "", label: "— Select type —" },
    ...(types || []).map((t) => ({
      value: String(t.id),
      label: t.description,
    })),
  ];

  async function submit() {
    setLoading(true);
    const branchId =
      branches.find((b) => b.name === d.branch)?.id ?? d.branch_id ?? null;
    const payload = {
      warehouse_name: d.name, 
      warehouse_type_id: d.warehouse_type_id ? Number(d.warehouse_type_id) : null,
      branch_id: branchId != null ? Number(branchId) : null,
      is_active: d.status === "Active" ? 1 : 0,
      warehouse_address: {
        address_code: d.address_code,
        address_id: d.address_id,
        address_line_1: d.addr1,
        address_line_2: d.addr2,
        city: d.city,
        region: d.region,
        postal_code: d.postal,
        country: d.country,
      },
    };
    await WarehouseAPI.update(warehouse.id, payload);
    onSave({ ...warehouse, ...d });
    setLoading(false);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Edit Warehouse"
        subtitle="Update warehouse details"
        subject={` ${warehouse.name}`}
        onBack={onBack}
      />
      <FormCard label="Warehouse Details">
        <FG label="Warehouse Name">
          <Inp value={d.name} onChange={(v) => upd("name", v)} />
        </FG>
        <div className="g2">
          <FG label="Branch">
            <Sel
              value={d.branch}
              onChange={(v) => upd("branch", v)}
              options={branchOpts}
            />
          </FG>
          <FG label="Warehouse Type">
            <Sel
              value={d.warehouse_type_id || ""}
              onChange={(v) => upd("warehouse_type_id", v)}
              options={typeOpts}
            />
          </FG>
        </div>
        <FG label="Status">
          <Sel
            value={d.status}
            onChange={(v) => upd("status", v)}
            options={[
              { value: "Active", label: "Active" },
              { value: "Maintenance", label: "Maintenance" },
              { value: "Inactive", label: "Inactive" },
            ]}
          />
        </FG>
      </FormCard>
      <FormCard icon="📍" label="Location & Address">
        <AddressMapPicker
          value={location || {}}
          onChange={(data) => {
            setLocation(data);
            if (data.addr1 != null) upd("addr1", data.addr1);
            if (data.addr2 != null) upd("addr2", data.addr2);
            if (data.city != null) upd("city", data.city);
            if (data.postal != null) upd("postal", data.postal);
            if (data.country != null) upd("country", data.country);
            if (data.region) upd("region", data.region);
          }}
          required
        />
        <div style={{ height: 12 }} />
        <div className="g2">
          <FG label="Address Code">
            <Inp
              value={d.address_code}
              onChange={(v) => upd("address_code", v)}
            />
          </FG>
          <FG label="City">
            <Inp value={d.city} onChange={(v) => upd("city", v)} />
          </FG>
        </div>
        <FG label="Address Line 1">
          <Inp value={d.addr1} onChange={(v) => upd("addr1", v)} />
        </FG>
        <FG label="Address Line 2">
          <Inp value={d.addr2} onChange={(v) => upd("addr2", v)} />
        </FG>
        <div className="g2">
          <FG label="Postal Code">
            <Inp value={d.postal} onChange={(v) => upd("postal", v)} />
          </FG>
        </div>
        <FG label="Country">
          <Inp value={d.country} onChange={(v) => upd("country", v)} />
        </FG>
        <div style={{ height: 8 }} />
        <FG label="Province">
          <Sel
            value={d.region || ""}
            onChange={(v) => upd("region", v)}
            options={SA_PROVINCES}
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Save Changes"
      />
    </div>
  );
}

// ─── DeleteWarehousePage ───────────────────────────────────────────────────────
function DeleteWarehousePage({ warehouse, onConfirm, onBack }) {
  const [loading, setLoading] = useState(false);
  async function confirm() {
    setLoading(true);
    await WarehouseAPI.delete(warehouse.id);
    onConfirm(warehouse.id);
    setLoading(false);
  }
  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Delete Warehouse"
        subtitle="Inventory data will be archived"
        subject={` ${warehouse.name}`}
        onBack={onBack}
      />
      <div className="danger-zone mb-16">
        <div className="danger-zone-title">⚠ Danger Zone</div>
        <div className="danger-zone-msg">
          This warehouse and all configuration will be permanently removed.
        </div>
      </div>
      <FormCard label="Warehouse to be Deleted">
        <ReviewBlock
          data={{
            Warehouse: warehouse.name,
            Location: warehouse.location,
            Branch: warehouse.branch,
            Status: warehouse.status,
          }}
        />
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={confirm}
        loading={loading}
        label="Confirm Delete"
        danger
      />
    </div>
  );
}

// ─── WarehouseTypesPage ────────────────────────────────────────────────────────
function WarehouseTypesPage({
  onBack,
  toast,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editDesc, setEditDesc] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t = await WarehouseAPI.getTypes();
    setTypes(t || []);
    setLoading(false);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function createType() {
    if (!newDesc.trim()) {
      alert("Enter a description.");
      return;
    }
    setCreating(true);
    const res = await WarehouseAPI.createType({
      description: newDesc.trim(),
      organization_id: getOrgIdFromToken(),
    });
    if (res) {
      toast("Warehouse type created", "success");
      setNewDesc("");
      await load();
    } else alert("Failed to create.");
    setCreating(false);
  }

  async function saveEdit(t) {
    const res = await WarehouseAPI.updateType(t.id, { description: editDesc });
    if (res) {
      toast("Type updated", "success");
      setEditId(null);
      await load();
    } else alert("Failed to update.");
  }
  async function deleteType(t) {
    if (!window.confirm(`Delete type "${t.description}"?`)) return;
    const res = await WarehouseAPI.deleteType(t.id);
    if (res) {
      toast("Type deleted", "warning");
      await load();
    } else alert("Failed to delete.");
  }
  async function viewDetail(t) {
    setDetailId(t.id);
    const res = await WarehouseAPI.getTypeById(t.id);
    setDetail(res);
  }

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Warehouse Types"
        subtitle="Manage all warehouse type classifications"
        onBack={onBack}
      />
      {/* Only show the Add form if canCreate */}
      {canCreate && (
        <FormCard label="Add New Type">
          <FG label="Description">
            <Inp
              value={newDesc}
              onChange={setNewDesc}
              placeholder="e.g. Cold Storage, Bulk, Hazmat"
            />
          </FG>
          <Btn variant="p" onClick={createType} disabled={creating}>
            {creating ? <span className="spin-icon" /> : "＋ Create Type"}
          </Btn>
        </FormCard>
      )}
      <FormCard label={`All Types (${types.length})`}>
        {loading ? (
          <div
            className="empty"
            style={{
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 80,
            }}
          >
            Loading…
          </div>
        ) : (
          <DataTable
            columns={[
              "Description",
              "ID",
              canUpdate || canDelete ? "Actions" : null,
            ].filter(Boolean)}
            rows={types.map((t) => (
              <tr key={t.id}>
                <td>
                  {editId === t.id && canUpdate ? (
                    <Inp value={editDesc} onChange={setEditDesc} />
                  ) : (
                    <span className="fw-6">{t.description}</span>
                  )}
                </td>
                <td className="mono tc3">{t.id}</td>
                {(canUpdate || canDelete) && (
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {editId === t.id && canUpdate ? (
                        <>
                          <Btn variant="p" sm onClick={() => saveEdit(t)}>
                            Save
                          </Btn>
                          <Btn variant="g" sm onClick={() => setEditId(null)}>
                            Cancel
                          </Btn>
                        </>
                      ) : (
                        <>
                          {/*<Btn variant="g" sm onClick={() => viewDetail(t)}>
                            View
                          </Btn>*/}
                          {canUpdate && (
                            <Btn
                              variant="s"
                              sm
                              onClick={() => {
                                setEditId(t.id);
                                setEditDesc(t.description);
                              }}
                            >
                              Edit
                            </Btn>
                          )}
                          {canDelete && (
                            <Btn variant="d" sm onClick={() => deleteType(t)}>
                              Delete
                            </Btn>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          />
        )}
      </FormCard>
      {detailId && (
        <Modal
          title="Warehouse Type Detail"
          onClose={() => {
            setDetailId(null);
            setDetail(null);
          }}
        >
          {!detail ? (
            <div
              className="empty"
              style={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 60,
              }}
            >
              Loading…
            </div>
          ) : (
            <ReviewBlock
              data={{
                ID: detail.warehouse_type_id || detail.id,
                Description: detail.description,
                "Organization ID": detail.organization_id,
                Created: detail.created_at || "-",
              }}
            />
          )}
        </Modal>
      )}
      <div className="fb" style={{ marginTop: 18 }}>
        <Btn variant="g" onClick={onBack}>
          Back
        </Btn>
      </div>
    </div>
  );
}

function UserWarehousesModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    WarehouseAPI.getByUser(user.id).then((res) => {
      setData(res);
      setLoading(false);
    });
  }, [user.id]);
  const arr = Array.isArray(data) ? data : data ? [data] : [];
  return (
    <Modal title={`Warehouses — ${user.name}`} onClose={onClose}>
      {loading ? (
        <div
          className="empty"
          style={{
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 60,
          }}
        >
          Loading…
        </div>
      ) : arr.length === 0 ? (
        <div
          className="empty"
          style={{
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 60,
          }}
        >
          No warehouses assigned to this user.
        </div>
      ) : (
        <DataTable
          columns={["Warehouse", "Branch", "Status"]}
          rows={arr.map((w, i) => (
            <tr key={i}>
              <td>{w.warehouse_name || w.name || "-"}</td>
              <td>{w.branch?.branch_name || w.branch || "-"}</td>
              <td>
                <Badge color={w.is_active ? "green" : "red"}>
                  {w.is_active ? "Active" : "Inactive"}
                </Badge>
              </td>
            </tr>
          ))}
        />
      )}
    </Modal>
  );
}

// ─── BindWarehousePage — gated to WAREHOUSE:UPDATE ────────────────────────────
function BindWarehousePage({
  users = [],
  warehouses = [],
  branches = [],
  onSave,
  onBack,
}) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [loading, setLoading] = useState(false);
  const branchOptions = [
    { value: "", label: "— All branches —" },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const warehouseOptions = [
    { value: "", label: "— Select warehouse —" },
    ...warehouses
      .filter((w) => !selectedBranch || String(w.branch_id) === selectedBranch)
      .map((w) => ({ value: String(w.id), label: `${w.name} (${w.branch})` })),
  ];
  async function submit() {
    if (!selectedUserId || !selectedWarehouse) {
      alert("Choose both a user and a warehouse.");
      return;
    }
    setLoading(true);
    const res = await WarehouseAPI.bind(
      Number(selectedWarehouse),
      Number(selectedUserId),
    );
    if (!res) alert("Failed to bind user to warehouse.");
    onSave(res || null);
    setLoading(false);
  }
  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Bind User to Warehouse"
        subtitle="Assign a user's access to a specific warehouse"
        onBack={onBack}
      />
      <FormCard label="Binding Details">
        <div className="g2">
          <FG label="Filter by Branch">
            <Sel
              value={selectedBranch}
              onChange={(v) => {
                setSelectedBranch(v);
                setSelectedWarehouse("");
              }}
              options={branchOptions}
            />
          </FG>
          <FG label="Warehouse">
            <Sel
              value={selectedWarehouse}
              onChange={setSelectedWarehouse}
              options={warehouseOptions}
            />
          </FG>
        </div>
        <FG label="User">
          <UserPicker
            users={users}
            label="Select user"
            value={selectedUserId}
            onChange={setSelectedUserId}
          />
        </FG>
      </FormCard>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Bind User to Warehouse"
      />
    </div>
  );
}

// ─── UnbindWarehousePage — gated to WAREHOUSE:UPDATE ──────────────────────────
function UnbindWarehousePage({
  users = [],
  warehouses = [],
  branches = [],
  onSave,
  onBack,
}) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [loading, setLoading] = useState(false);
  const branchOptions = [
    { value: "", label: "— All branches —" },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const warehouseOptions = [
    { value: "", label: "— Select warehouse —" },
    ...warehouses
      .filter((w) => !selectedBranch || String(w.branch_id) === selectedBranch)
      .map((w) => ({ value: String(w.id), label: `${w.name} (${w.branch})` })),
  ];
  async function submit() {
    if (!selectedUserId || !selectedWarehouse) {
      alert("Choose both a user and a warehouse.");
      return;
    }
    setLoading(true);
    const res = await WarehouseAPI.unbind(
      Number(selectedWarehouse),
      Number(selectedUserId),
    );
    if (!res) alert("Failed to unbind warehouse.");
    onSave(res || null);
    setLoading(false);
  }
  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Unbind User from Warehouse"
        subtitle="Remove a user's access to a specific warehouse"
        onBack={onBack}
      />
      <FormCard label="Unbind Details">
        <div className="g2">
          <FG label="Filter by Branch">
            <Sel
              value={selectedBranch}
              onChange={(v) => {
                setSelectedBranch(v);
                setSelectedWarehouse("");
              }}
              options={branchOptions}
            />
          </FG>
          <FG label="Warehouse">
            <Sel
              value={selectedWarehouse}
              onChange={setSelectedWarehouse}
              options={warehouseOptions}
            />
          </FG>
        </div>
        <FG label="User">
          <UserPicker
            users={users}
            label="Select user"
            value={selectedUserId}
            onChange={setSelectedUserId}
          />
        </FG>
      </FormCard>
      <div className="danger-zone mb-16" style={{ marginTop: 0 }}>
        <div className="danger-zone-msg">
          This will revoke the user's access to the selected warehouse.
        </div>
      </div>
      <FormFooter
        onCancel={onBack}
        onSubmit={submit}
        loading={loading}
        label="Unbind User"
        danger
      />
    </div>
  );
}

function ViewUserWarehousePage({ users, onBack }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const arr = data
    ? Array.isArray(data)
      ? data
      : data?.warehouses || [data]
    : [];
  async function lookup() {
    if (!selectedUserId) return;
    setLoading(true);
    const res = await WarehouseAPI.getByUser(Number(selectedUserId));
    setData(res);
    setLoading(false);
  }
  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <PageHeader
        title="Warehouses by User"
        subtitle="Look up which warehouses a user is assigned to"
        onBack={onBack}
      />
      <FormCard label="Lookup">
        <FG label="User">
          <UserPicker
            users={users}
            label="Select user"
            value={selectedUserId}
            onChange={setSelectedUserId}
          />
        </FG>
        <Btn variant="p" onClick={lookup} disabled={loading || !selectedUserId}>
          {loading ? <span className="spin-icon" /> : "Look Up Warehouses"}
        </Btn>
      </FormCard>
      {data !== null && (
        <FormCard label={`Results (${arr.length})`}>
          {arr.length === 0 ? (
            <div
              className="empty"
              style={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 60,
              }}
            >
              No warehouses found for this user.
            </div>
          ) : (
            <DataTable
              columns={["Warehouse", "Branch", "Type", "Status"]}
              rows={arr.map((w, i) => (
                <tr key={i}>
                  <td className="fw-6">{w.warehouse_name || w.name || "-"}</td>
                  <td>{w.branch?.branch_name || "-"}</td>
                  <td>{w.warehouse_type?.description || "-"}</td>
                  <td>
                    <Badge color={w.is_active ? "green" : "red"}>
                      {w.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                </tr>
              ))}
            />
          )}
        </FormCard>
      )}
      <div className="fb" style={{ marginTop: 18 }}>
        <Btn variant="g" onClick={onBack}>
          Back
        </Btn>
      </div>
    </div>
  );
}

// ─── WarehousesSection — gate buttons by permissions ──────────────────────────
function WarehousesSection({
  warehouses,
  branches,
  users,
  onNavigate,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const [search, setSearch] = useState("");
  const [branchId, setBranchId] = useState("");
  const [list, setList] = useState(warehouses);
  const [loadingFilter, setLoadingFilter] = useState(false);
  useEffect(() => {
    setList(warehouses);
  }, [warehouses]);
  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setList(warehouses);
      return;
    }
    setLoadingFilter(true);
    WarehouseAPI.getByBranch(branchId).then((result) => {
      if (cancelled) return;
      setList(result || []);
      setLoadingFilter(false);
    });
    return () => {
      cancelled = true;
    };
  }, [branchId, warehouses]);

  const filtered = list.filter((w) =>
    `${w.name} ${w.location} ${w.branch}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div className="panel">
      <div className="tbar">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search warehouses…"
        />
        <Sel
          value={branchId}
          onChange={setBranchId}
          options={[
            { value: "", label: "All branches" },
            ...branches.map((b) => ({ value: String(b.id), label: b.name })),
          ]}
        />
        <div className="flex-1" />
        <Btn variant="g" sm onClick={() => onNavigate("viewUserWarehouse")}>
          View by User
        </Btn>
        <Btn variant="g" sm onClick={() => onNavigate("warehouseTypes")}>
          Types
        </Btn>
        {/* Bind/unbind requires UPDATE permission */}
        {canUpdate && (
          <Btn variant="g" sm onClick={() => onNavigate("bindWarehouse")}>
            Bind User
          </Btn>
        )}
        {canUpdate && (
          <Btn variant="g" sm onClick={() => onNavigate("unbindWarehouse")}>
            Unbind User
          </Btn>
        )}
        {/* Add warehouse requires CREATE permission */}
        {canCreate && (
          <Btn variant="p" sm onClick={() => onNavigate("addWarehouse")}>
            Add Warehouse
          </Btn>
        )}
      </div>
      {loadingFilter ? (
        <div
          className="empty"
          style={{
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 80,
          }}
        >
          Loading…
        </div>
      ) : (
        <DataTable
          columns={[
            "Warehouse",
            "Type",
            "Branch",
            "Status",
            canUpdate || canDelete ? "Actions" : null,
          ].filter(Boolean)}
          rows={filtered.map((w) => (
            <tr key={w.id}>
              <td className="fw-6">{w.name}</td>
              <td>{w.location}</td>
              <td>{w.branch}</td>
              <td>
                <Badge
                  color={
                    w.status === "Active"
                      ? "green"
                      : w.status === "Maintenance"
                        ? "amber"
                        : "red"
                  }
                >
                  {w.status}
                </Badge>
              </td>
              {(canUpdate || canDelete) && (
                <td>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {canUpdate && (
                      <Btn
                        variant="s"
                        sm
                        onClick={() => onNavigate("editWarehouse", w)}
                      >
                        Edit
                      </Btn>
                    )}
                    {canDelete && (
                      <Btn
                        variant="d"
                        sm
                        onClick={() => onNavigate("deleteWarehouse", w)}
                      >
                        Delete
                      </Btn>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        />
      )}
    </div>
  );
}

// ─── Root ──────────────────────────────────────────────────────────────────────
export default function Warehouses({ showToast: externalToast }) {
  const [page, setPage] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [toasts, setToasts] = useState([]);

  function toast(msg, type = "info") {
    if (externalToast) externalToast(msg, type);
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }

  const loadAll = useCallback(async () => {
    const [w, b, u] = await Promise.all([
      WarehouseAPI.getAll(),
      BranchAPI.getAll(),
      UserAPI.getAll(),
    ]);
    setWarehouses(w || []);
    setBranches(b || []);
    setUsers(u || []);
  }, []);
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Permissions ───────────────────────────────────────────────────────────
  const { canSeePage, can, ready } = usePermissions();
  if (ready && !canSeePage("warehouses")) {
    return (
      <div className="panel" style={{ padding: 24 }}>
        <h2>Access denied</h2>
        <p className="tc3">You do not have permission to view warehouses.</p>
      </div>
    );
  }

  const canCreate = ready && can("WAREHOUSE", "CREATE");
  const canUpdate = ready && can("WAREHOUSE", "UPDATE");
  const canDelete = ready && can("WAREHOUSE", "DELETE");

  function navigate(view, data = null) {
    setPage({ view, data });
  }
  function goBack() {
    setPage(null);
  }

  if (page) {
    const { view, data } = page;
    if (view === "addWarehouse" && canCreate)
      return (
        <AddWarehousePage
          branches={branches}
          onSave={async () => {
            toast("Warehouse created", "success");
            goBack();
            setWarehouses((await WarehouseAPI.getAll()) || []);
          }}
          onBack={goBack}
        />
      );
    if (view === "editWarehouse" && canUpdate)
      return (
        <EditWarehousePage
          warehouse={data}
          branches={branches}
          onSave={async () => {
            toast("Warehouse updated", "success");
            goBack();
            setWarehouses((await WarehouseAPI.getAll()) || []);
          }}
          onBack={goBack}
        />
      );
    if (view === "deleteWarehouse" && canDelete)
      return (
        <DeleteWarehousePage
          warehouse={data}
          onConfirm={async () => {
            toast("Warehouse deleted", "warning");
            goBack();
            setWarehouses((await WarehouseAPI.getAll()) || []);
          }}
          onBack={goBack}
        />
      );
    if (view === "warehouseTypes")
      return (
        <WarehouseTypesPage
          toast={toast}
          onBack={goBack}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      );
    if (view === "bindWarehouse" && canUpdate)
      return (
        <BindWarehousePage
          users={users}
          warehouses={warehouses}
          branches={branches}
          onSave={() => {
            toast("User bound to warehouse", "success");
            goBack();
          }}
          onBack={goBack}
        />
      );
    if (view === "unbindWarehouse" && canUpdate)
      return (
        <UnbindWarehousePage
          users={users}
          warehouses={warehouses}
          branches={branches}
          onSave={() => {
            toast("User unbound from warehouse", "success");
            goBack();
          }}
          onBack={goBack}
        />
      );
    if (view === "viewUserWarehouse")
      return <ViewUserWarehousePage users={users} onBack={goBack} />;
    goBack();
    return null;
  }

  return (
    <div className="page">
      <WarehousesSection
        warehouses={warehouses}
        branches={branches}
        users={users}
        onNavigate={navigate}
        canCreate={canCreate}
        canUpdate={canUpdate}
        canDelete={canDelete}
      />
      <div className="toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}